import axios, { type AxiosProgressEvent } from 'axios';
import { type SexUpper } from 'primary-schema';

import { type Common } from './common.js';
import { type SuccessResponse } from './models/common.js';
import type * as DigitalCheckIn from './models/digitalCheckIn.js';
import {
  type EncounterData,
  type EncounterDataMaybeWithMinimalFormSet,
  type HasEncounterOrPrevisitFormSetId,
} from './models/encounter.js';
import { type ProvisionalPatient } from './models/index.js';
import type * as Log from './models/log.js';
import { type NoteFieldWithConfig } from './models/note/field.js';
import { type PatientNotification } from './models/patient/notification.js';
import type * as Patient from './models/patient.js';
import type * as PatientCallbackRequest from './models/patientCallbackRequest.js';
import { type ThemeResponse } from './models/practice/theme.js';
import { type AuthPatientBody } from './models/previsit/auth/patient.js';
import { type PrevisitFormFieldWithConfig } from './models/previsit/formField.js';
import {
  type PrevisitFormSetWithFormsAndAuthCode,
  type PrevisitFormSetsResponse,
} from './models/previsit/formSet.js';
import type * as PrevisitFormTemplate from './models/previsit/formTemplate.js';
import {
  type DisplayablePrevisitFormTemplate,
  type PrevisitFormTemplateWithParametersAndConfigs,
} from './models/previsit/formTemplate.js';
import {
  type BasePrevisitFormTemplateFieldConfig,
  type PrevisitFormTemplateFieldConfig,
  type PrevisitFormTemplateFieldConfigWithConditions,
  type UpsertValues as UpsertFieldConfig,
} from './models/previsit/formTemplateFieldConfig.js';
import {
  type BasePrevisitFormTemplatePageConfig,
  type PrevisitFormTemplatePageConfig,
} from './models/previsit/formTemplatePageConfig.js';
import { Language } from './models/previsit/language.js';
import {
  type ContactVerificationSource,
  type ProvisionalFormSetData,
  type ReconciliationStatus,
  type RegistrationContactVerification,
} from './models/previsit/register.js';
import {
  type FindAllOptions,
  type PatientData,
  type ProviderAssetSignedUrls,
} from './models/previsit.js';
import type * as Timeline from './models/timeline.js';
import type * as Workflow from './models/workflow.js';

export interface NotificationTimelineItem {
  notificationConfigId: string | null;
  notificationContentId: string | null;
  contentName: string | null;
  reason: string | null;
  modality: string;
  activeTimestamp: string;
  notification: PatientNotification | null;
  status: 'sent' | 'scheduled' | 'failed';
}

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/previsit';
  }

  // admin endpoints
  // Deprecated: Should be removed and replaced with fetchAll in web/vivaa-api/src/previsitFormTemplate.ts
  async fetchPrevisitFormTemplates(
    options: FindAllOptions = {}
  ): Promise<{ previsitFormTemplates: DisplayablePrevisitFormTemplate[] }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-templates`, options);
    return await this.common.getJSON(url);
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async createFormTemplate(
    createValues: PrevisitFormTemplate.CreateValues
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplateWithLocalizations> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-templates`);
    return await this.common.postJSON(url, createValues);
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async createFormTemplatePreview(
    formTemplateId: string,
    templateFieldConfigs: BasePrevisitFormTemplateFieldConfig[]
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplatePreview> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}/preview`
    );
    return await this.common.postJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async updateFormTemplate(
    formTemplateId: string,
    updateValues: PrevisitFormTemplate.UpdateValues
  ): Promise<{
    template: PrevisitFormTemplate.PrevisitFormTemplateWithLocalizations;
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-templates/${formTemplateId}`);
    return await this.common.patchJSON(url, updateValues);
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async createFormTemplateFieldConfigs(
    formTemplateId: string,
    templateFieldConfigs: BasePrevisitFormTemplateFieldConfig[]
  ): Promise<PrevisitFormTemplateFieldConfig[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}/field-configs`
    );
    return await this.common.postJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async updateFormTemplateFieldConfig(
    formTemplateId: string,
    formTemplateFieldConfigId: string,
    updateValues: Partial<BasePrevisitFormTemplateFieldConfig>
  ): Promise<PrevisitFormTemplateFieldConfig> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}/field-configs/${formTemplateFieldConfigId}`
    );
    return await this.common.patchJSON(url, updateValues);
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async updateFormTemplateFieldConfigs(
    formTemplateId: string,
    templateFieldConfigs: UpsertFieldConfig[]
  ): Promise<PrevisitFormTemplateFieldConfigWithConditions[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}/field-configs`
    );
    return await this.common.putJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async upsertFormTemplatePageConfigs(
    formTemplateId: string,
    templatePageConfigs: BasePrevisitFormTemplatePageConfig[]
  ): Promise<PrevisitFormTemplatePageConfig[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}/page-configs`
    );
    return await this.common.postJSON(url, {
      pages: templatePageConfigs,
    });
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async deleteFormTemplate(id: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-templates/${id}`);
    return await this.common.deleteJSON(url);
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async deleteFormTemplateLanguage(id: string, languageId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${id}/languages/${languageId}`
    );
    return await this.common.deleteJSON(url);
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async fetchAllLabels(): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-templates/labels`);
    return await this.common.getJSON(url);
  }

  // analyst app endpoints
  async fetchPrevisitNoteFields(encounterId: string): Promise<{
    hpi: NoteFieldWithConfig[];
    previsit: NoteFieldWithConfig[];
  }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/encounters/${encounterId}/note-sections`
    );
    return await this.common.getJSON(url);
  }

  async expireFormSets(formSetIds: string[]): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-sets/expire`);
    return await this.common.postJSON(url, { previsitFormSetIds: formSetIds });
  }

  async sendAll(
    formSetId: string,
    checkRateLimiter: boolean,
    checkDuplicates: boolean = true
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-sets/${formSetId}/sendAll`);
    return await this.common.postJSON(url, { checkRateLimiter, checkDuplicates });
  }

  async sendTextMessage(
    formSetId: string,
    checkRateLimiter: boolean,
    checkDuplicates: boolean = true
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-sets/${formSetId}/message`);
    return await this.common.postJSON(url, { checkRateLimiter, checkDuplicates });
  }

  async sendEmailMessage(
    formSetId: string,
    checkRateLimiter: boolean,
    checkDuplicates: boolean = true
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-sets/${formSetId}/email`);
    return await this.common.postJSON(url, { checkRateLimiter, checkDuplicates });
  }

  async callVoice(
    formSetId: string,
    checkRateLimiter: boolean,
    checkDuplicates: boolean = true
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-sets/${formSetId}/call`);
    return await this.common.postJSON(url, { checkRateLimiter, checkDuplicates });
  }

  // Deprecate
  async fetchFormByEncounterId(args: {
    encounterId: string;
    includeAuthenticationCode?: boolean;
    includeFieldHistory?: boolean;
  }): Promise<PrevisitFormSetWithFormsAndAuthCode> {
    const { encounterId, includeAuthenticationCode, includeFieldHistory } = args;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/encounters/${encounterId}/form-sets`,
      { includeAuthenticationCode, includeFieldHistory }
    );
    return await this.common.getJSON(url);
  }

  async fetchAllFormSetsByEhrInformationOrEncounterId(args: {
    encounterId?: string;
    ehrInformationId?: string;
    includeAuthenticationCode?: boolean;
  }): Promise<PrevisitFormSetWithFormsAndAuthCode[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/form-sets`, args);
    const { previsitFormSets } = await this.common.getJSON(url);
    return previsitFormSets;
  }

  async fetchFrontDeskAuthCode(ehrInformationId: string): Promise<{ code: string | null }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/ehr-information/${ehrInformationId}/previsit-auth-code`
    );
    return await this.common.getJSON(url);
  }

  async fetchDigitalCheckInAuthCodeForEncounter(
    encounterId: string
  ): Promise<{ code: string | null }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/encounters/${encounterId}/digital-check-in-auth-code`
    );
    return await this.common.getJSON(url);
  }

  async fetchEncounterPrevisitUrl(
    encounterId: string
  ): Promise<{ encounterPrevisitUrl: string | null }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/encounters/${encounterId}/previsit-url`
    );
    return await this.common.getJSON(url);
  }

  async fetchPatientPrevisitUrl(
    ehrInformationId: string
  ): Promise<{ patientPrevisitUrl: string | null }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/ehr-information/${ehrInformationId}/previsit-url`
    );
    return await this.common.getJSON(url);
  }

  async fetchPatientNotifications(
    formSetId: string,
    includeContent?: boolean
  ): Promise<PatientNotification[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-sets/${formSetId}/patient-notifications`,
      { includeContent }
    );
    return await this.common.getJSON(url);
  }

  async fetchScheduledNotifications(formSetId?: string): Promise<NotificationTimelineItem[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-sets/${formSetId}/scheduled-notifications`
    );
    return await this.common.getJSON(url);
  }

  async fetchNotificationTimelineByEncounterId(
    encounterId: string
  ): Promise<NotificationTimelineItem[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/notification-timeline`, {
      encounterId,
    });
    return await this.common.getJSON(url);
  }

  async fetchTimelineEvents(args: HasEncounterOrPrevisitFormSetId): Promise<Timeline.Event[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/timeline`, args);
    return await this.common.getJSON(url);
  }

  async exportForm(encounterId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export/form`);
    return await this.common.postJSON(url, { encounterId });
  }

  async createWorkflow(
    encounterId: string,
    status: Workflow.Previsit.Status
  ): Promise<Workflow.Previsit.Status> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/encounters/${encounterId}/workflows`
    );
    const workflow = await this.common.postJSON(url, { status });
    return workflow.status;
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async fetchPrevisitFormTemplate(
    formTemplateId: string,
    parseParameters?: boolean
  ): Promise<PrevisitFormTemplateWithParametersAndConfigs> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}`,
      parseParameters ? { parseParameters } : undefined
    );
    return await this.common.getJSON(url);
  }

  async createForm(
    encounterId: string,
    formTemplates: {
      [formTemplateId: string]: PrevisitFormTemplate.Parameters;
    }
  ): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/encounters/${encounterId}/form-sets`
    );
    return await this.common.postJSON(url, {
      formTemplates,
    });
  }

  async deleteForm(previsitFormId: string): Promise<{
    removed: { previsitFormId: string; previsitFormFieldTotal: number };
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/forms/${previsitFormId}`);
    return await this.common.deleteJSON(url);
  }

  async createAssetSignedUrl({
    fileName,
    configKey,
    contentType,
    override = false,
    ensureUniqueFileName = false,
  }: {
    fileName: string;
    configKey: string;
    contentType?: string;
    override?: boolean;
    ensureUniqueFileName?: boolean;
  }): Promise<{ signedUrl: string; publicUrl: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/assets/upload-url`);
    return await this.common.postJSON(url, {
      fileName,
      override,
      ensureUniqueFileName,
      contentType,
      configKey,
    });
  }

  async createProviderAssetSignedUrls(
    fileNames: string[],
    override: boolean = false
  ): Promise<ProviderAssetSignedUrls[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/assets/upload-urls`);
    return await this.common.postJSON(url, { fileNames, override });
  }

  async uploadAsset({
    file,
    signedUploadURL,
    onUploadProgress,
    contentType = 'image/png',
  }: {
    file: Blob;
    signedUploadURL: string;
    onUploadProgress?: (event: AxiosProgressEvent) => void;
    contentType?: string;
  }): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: { 'Content-Type': contentType },
    });
    return response.status;
  }

  // previsit web app endpoints
  async createPrevisitLog(args: {
    auditLog: Log.PrevisitAuditAction;
    previsitLog: Log.PrevisitAction;
    authenticationCode?: string;
    pathName?: string;
    source?: string;
  }): Promise<SuccessResponse> {
    const { auditLog, previsitLog, authenticationCode, pathName, source } = args;
    const url = this.common.constructPath('/patient-previsit/log');
    return await this.common.postJSON(url, {
      auditLog,
      previsitLog,
      authenticationCode,
      pathName,
      source,
    });
  }

  async unsubscribeEmail(unsubscribeCode: string): Promise<SuccessResponse> {
    const url = this.common.constructPath('/patient-previsit/unsubscribe');
    return await this.common.postJSON(url, {
      code: unsubscribeCode,
    });
  }

  async subscribeEmail(unsubscribeCode: string): Promise<SuccessResponse> {
    const url = this.common.constructPath('/patient-previsit/subscribe');
    return await this.common.postJSON(url, { code: unsubscribeCode });
  }

  async fetchFormSets(
    authenticationCode: string,
    lang: Language = Language.En,
    future?: boolean,
    patientInfo?: boolean
  ): Promise<PrevisitFormSetsResponse> {
    const url = this.common.constructPath(`/patient-previsit/form-sets`, {
      authenticationCode,
      lang: lang !== Language.En ? lang : undefined, // so we avoid joining localization tables when not required
      v2: true,
      future,
      patientInfo,
    });
    return await this.common.getJSON(url, {
      headers: { 'cache-control': 'no-cache' },
    });
  }

  async fetchEncounter(args: { authenticationCode: string }): Promise<EncounterData>;
  async fetchEncounter(args: {
    startTime: Date;
    ehrReferenceValue: string;
  }): Promise<EncounterDataMaybeWithMinimalFormSet>;
  async fetchEncounter(
    args: { authenticationCode: string } | { startTime: Date; ehrReferenceValue: string }
  ): Promise<EncounterData | EncounterDataMaybeWithMinimalFormSet> {
    const url = this.common.constructPath(`/patient-previsit/encounter`, args);
    return await this.common.getJSON(url);
  }

  async fetchPatient(authenticationCode: string): Promise<PatientData> {
    const url = this.common.constructPath(`/patient-previsit/patient`, {
      authenticationCode,
    });
    return await this.common.getJSON(url);
  }

  getPassbookUrl(formSetId: string): string {
    return this.common.constructPath(`/patient-previsit/passbook/${formSetId}`);
  }

  getPayPassUrl(formSetId: string): string {
    return this.common.constructPath(`/patient-previsit/paypass/${formSetId}`);
  }

  async updateFormField(formSetId: string, formFieldId: string, value: string) {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/fields/${formFieldId}`
    );
    return await this.common.patchJSON(url, { value });
  }

  async updateFormFields(formSetId: string, fields: PrevisitFormFieldWithConfig[]): Promise<void> {
    const url = this.common.constructPath(`/patient-previsit/form-sets/${formSetId}/fields`);
    await this.common.patchJSON(url, { fields });
  }

  async startFormSet(formSetId: string, authenticationCode: string) {
    const url = this.common.constructPath(`/patient-previsit/form-sets/${formSetId}/start`, {
      authenticationCode,
    });
    try {
      await this.common.postJSON(url, {});
    } catch (err) {
      console.warn(`form ${formSetId} has already been started`);
    }
  }

  async submitFormSet(formSetId: string, authenticationCode: string) {
    const url = this.common.constructPath(`/patient-previsit/form-sets/${formSetId}/submit`, {
      authenticationCode,
    });
    try {
      await this.common.postJSON(url, {});
    } catch (err) {
      console.warn(`form ${formSetId} has already been submitted`);
    }
  }

  async startForm(formId: string) {
    const url = this.common.constructPath(`/patient-previsit/forms/${formId}/started`);
    return await this.common.postJSON(url, {});
  }

  async completeForm(formId: string) {
    const url = this.common.constructPath(`/patient-previsit/forms/${formId}/completed`);
    return await this.common.postJSON(url, {});
  }

  async updateFormScore({
    formId,
    score,
    scores,
  }: {
    formId: string;
    score: number;
    scores: Record<string, number>;
  }) {
    const url = this.common.constructPath(`/patient-previsit/forms/${formId}/score`);
    return await this.common.postJSON(url, { score, scores });
  }

  async getLoginChallenge(
    authenticationCode: string,
    lastName: string,
    dateOfBirth: string
  ): Promise<{ zips: string[] }> {
    const url = this.common.constructPath('/patient-previsit/auth/challenge', {
      authenticationCode,
    });
    return await this.common.postJSON(url, { lastName, dateOfBirth });
  }

  async getContacts(args: {
    authenticationCode: string;
    dateOfBirth: string;
    lastName: string;
    zip?: string;
    multi?: boolean;
  }): Promise<{ email: string | null; phoneNumber: string | null }> {
    const url = this.common.constructPath('/patient-previsit/auth/contacts', args);
    return await this.common.getJSON(url);
  }

  async generateCode(args: {
    authenticationCode: string;
    dateOfBirth: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    zip?: string;
    multi?: boolean;
  }): Promise<boolean> {
    const url = this.common.constructPath('/patient-previsit/auth/code');
    const { success } = await this.common.postJSON(url, args);
    return success;
  }

  async maxLoginsExceeded(authenticationCode: string): Promise<boolean> {
    const url = this.common.constructPath('/patient-previsit/auth/limit', {
      authenticationCode,
    });
    const { limitReached } = await this.common.getJSON(url);
    return limitReached;
  }

  async isAuthenticated(): Promise<boolean> {
    const url = this.common.constructPath('/patient-previsit/auth/is-authenticated');
    const { isAuthenticated } = await this.common.getJSON(url);
    return isAuthenticated;
  }

  async getSessionStatus(): Promise<{
    isAuthenticated: boolean;
    sessionExpirationDate: string;
    timeRemaining: number;
    sessionMaxAge: number;
  }> {
    const url = this.common.constructPath('/patient-previsit/auth/session/status');
    return await this.common.getJSON(url);
  }

  async extendSession(): Promise<void> {
    const url = this.common.constructPath('/patient-previsit/auth/session/extend');
    await this.common.postJSON(url, {});
  }

  async login(args: {
    authenticationCode: string;
    lastName: string;
    dateOfBirth: string;
    zip?: string;
    multi?: boolean;
  }): Promise<{ patient: Patient.BasePatientWithEhrInformationId }> {
    const url = this.common.constructPath('/patient-previsit/auth/login');
    return await this.common.postJSON(url, args);
  }

  async preAuthLogin(
    authenticationCode: string,
    authBypassCode: string,
    multi?: boolean
  ): Promise<{ patient: Patient.BasePatientWithEhrInformationId }> {
    const url = this.common.constructPath('/patient-previsit/auth/pre-auth-login');
    return await this.common.postJSON(url, {
      authenticationCode,
      authBypassCode,
      multi,
    });
  }

  async receiptLogin(
    authenticationCode: string,
    lastName: string,
    dateOfBirth: Date
  ): Promise<{ patient: Patient.BasePatientWithEhrInformationId }> {
    const url = this.common.constructPath('/patient-previsit/auth/receipt-login');
    return await this.common.postJSON(url, {
      authenticationCode,
      lastName,
      dateOfBirth,
    });
  }

  async register(payload: {
    registrationCode: string;
    patientCode?: string;
    domain: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    cellPhoneNumber?: string;
    zipCode?: string;
    email?: string;
    sex?: SexUpper;
    practiceId?: string;
    previsitFormTemplateFieldConfigId?: string;
    provisionalPatientId?: string;
    assistantId?: string;
  }): Promise<
    | {
        authenticationCode: string;
      }
    | RegistrationContactVerification
  > {
    const url = this.common.constructPath('/patient-previsit/auth/register/login');
    return await this.common.postJSON(url, payload, 'POST', undefined, true);
  }

  async requestContactVerification(payload: {
    source: ContactVerificationSource;
    provisionalPatientId: string;
    assistantId?: string;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath('/patient-previsit/auth/register/contact-verification');
    return await this.common.postJSON(url, payload);
  }

  async findOrCreateProvisionalFormSet(payload: {
    urlIdentifier: string;
    domain: string;
    source?: string;
    assistantId?: string;
    language?: Language;
  }): Promise<ProvisionalFormSetData> {
    const url = this.common.constructPath('/patient-previsit/auth/register/provisional');
    return await this.common.postJSON(url, payload, 'POST', undefined, true);
  }

  async requestMessage(phoneNumber: string, refCode?: string): Promise<{ success: boolean }> {
    const url = this.common.constructPath('/patient-previsit/request-message');
    return await this.common.postJSON(url, { phoneNumber, refCode });
  }

  async verifyCodeAndLogin(payload: {
    code: string;
    provisionalPatientId: string;
    assistantId?: string;
  }): Promise<{
    authenticationCode: string;
    reconciliationStatus: ReconciliationStatus;
  }> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/register/contact-verification/verify'
    );
    return await this.common.postJSON(url, payload);
  }

  async logout() {
    const url = this.common.constructPath(`/patient-previsit/auth/logout`);
    return await this.common.postJSON(url, {});
  }

  async getThemeForLogin(args: {
    domain: string;
    authCode?: string;
    registrationCode?: string;
    provisionalAuthCode?: string;
    requestCode?: string;
    encounterId?: string;
  }): Promise<ThemeResponse> {
    const url = this.common.constructPath(`/patient-previsit/theme`, args);
    return await this.common.getJSON(url);
  }

  async getScheduleLink(previsitFormSetId: string): Promise<string> {
    const url = this.common.constructPath(`/patient-previsit/schedule-link`, {
      previsitFormSetId,
    });
    const { link } = await this.common.getJSON(url);
    return link;
  }

  async getReceiptInfo(
    authenticationCode: string
  ): Promise<{ receiptUrl: string; encounterData: EncounterData }> {
    const url = this.common.constructPath(`/patient-previsit/receipt/${authenticationCode}/info`);
    return await this.common.getJSON(url);
  }

  async createPatientCallbackRequest(
    payload: PatientCallbackRequest.CreateValues
  ): Promise<PatientCallbackRequest.PatientCallbackRequest> {
    const url = this.common.constructPath('/patient-previsit/patient-callback-requests');
    return await this.common.putJSON(url, payload);
  }

  async getIsPatientAuthenticated(configurationId: string): Promise<{
    isAuthenticated: boolean;
  }> {
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/is-authenticated`
    );
    return await this.common.getJSON(url, undefined, true);
  }

  async logoutAssistant(configurationId: string) {
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/logout`
    );
    return await this.common.postJSON(url, {}, 'POST');
  }

  async authPatientForAssistant(args: {
    loginInfo: AuthPatientBody;
    configurationId: string;
    sessionId: string;
  }): Promise<{
    provisionalPatient: ProvisionalPatient.ProvisionalPatient;
    contactVerification: boolean;
  }> {
    const { configurationId, loginInfo, sessionId } = args;
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/login`
    );
    return await this.common.postJSON(url, { ...loginInfo, sessionId }, 'POST', undefined, true);
  }

  async verifyAuthPatientForAssistant(args: {
    configurationId: string;
    provisionalPatientId: string;
    code: string;
    sessionId: string;
  }): Promise<{
    success: boolean;
    reconciliationStatus: ReconciliationStatus;
  }> {
    const { configurationId, provisionalPatientId, code, sessionId } = args;
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/verify`
    );
    return await this.common.postJSON(url, { provisionalPatientId, code, sessionId }, 'POST');
  }

  async generateConsentLink(args: { encounterId: string }): Promise<string> {
    const url = this.common.constructPath('/patient-previsit/musc/helix-login');
    const { consentLink } = await this.common.postJSON(url, args);
    return consentLink;
  }

  // Deprecated: Should be removed and replaced with the function in web/vivaa-api/src/previsitFormTemplate.ts
  async findHistory(
    formTemplateId: string
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplateHistory[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/form-templates/${formTemplateId}/history`
    );
    const { history } = await this.common.getJSON(url);
    return history;
  }

  async checkIn(args: { previsitFormSetId: string }): Promise<any> {
    const url = this.common.constructPath(`/patient-previsit/front-desk/check-in`);
    return await this.common.postJSON(url, args);
  }

  async fetchDigitalCheckInDetails({
    authCode,
    encounterId,
  }: {
    authCode: string;
    encounterId: string;
  }): Promise<DigitalCheckIn.DigitalCheckInDetails> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/${encounterId}/digital-check-in`,
      {
        authCode,
      }
    );

    return await this.common.getJSON(url);
  }

  async digitalCheckInEncounter({
    encounterId,
    authCode,
  }: {
    encounterId: string;
    authCode: string;
  }): Promise<void> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/${encounterId}/digital-check-in`
    );

    return await this.common.postJSON(url, { authCode });
  }
}
